<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import type { SchoolClassDetail } from 'src/modules/School/composables/useSchoolData';
import { schoolUserDisplayName } from 'src/modules/School/utilities/schoolUserDisplayName';
import { lastActiveLabel } from 'src/modules/School/utilities/lastActiveLabel';
import SchoolEnrollmentStatusBadge from 'src/modules/School/components/SchoolEnrollmentStatusBadge/SchoolEnrollmentStatusBadge.vue';
import {
  mdiChartBox,
  mdiAccountRemoveOutline,
  mdiAccountReactivateOutline,
} from '@quasar/extras/mdi-v7';

const props = defineProps<{
  classDetail: SchoolClassDetail;
  /**
   * Teachers of this class can approve/decline pending applications, drop
   * active students, and reinstate dropped/declined ones.
   */
  canDecide?: boolean;
}>();

const emit = defineEmits<{
  decide: [
    enrollmentDocumentId: string,
    decision: 'approve' | 'decline' | 'drop' | 'reinstate',
  ];
  'view-stats': [studentDocumentId: string, studentName: string];
}>();

const $q = useQuasar();
const { t, locale } = useI18n();

function lastActiveText(lastActiveAt: string | null | undefined): string {
  const label = lastActiveLabel(lastActiveAt, locale.value);
  return label
    ? t('SCHOOL_LAST_ACTIVE', { when: label })
    : t('SCHOOL_NEVER_ACTIVE');
}

type EnrollmentLike = { status?: string; enrollment_status?: string };

function enrollmentStatus(enrollment: EnrollmentLike): string {
  return enrollment.status || enrollment.enrollment_status || '';
}

function isPending(enrollment: EnrollmentLike): boolean {
  return enrollmentStatus(enrollment) === 'pending';
}

function isActive(enrollment: EnrollmentLike): boolean {
  return enrollmentStatus(enrollment) === 'active';
}

function isReinstatable(enrollment: EnrollmentLike): boolean {
  const status = enrollmentStatus(enrollment);
  return status === 'dropped' || status === 'declined';
}

// Status filter: the roster defaults to the rows a teacher acts on (active +
// pending); dropped/declined/completed stay one tap away so a student who was
// told "ask your teacher to reinstate you" can be found and reinstated.
const STATUS_ORDER = [
  'active',
  'pending',
  'dropped',
  'declined',
  'completed',
] as const;
const visibleStatuses = ref<string[]>(['active', 'pending']);

const statusCounts = computed(() => {
  const counts: Record<string, number> = {};
  for (const enrollment of props.classDetail.enrollments || []) {
    const status = enrollmentStatus(enrollment);
    counts[status] = (counts[status] || 0) + 1;
  }
  return counts;
});

const visibleEnrollments = computed(() =>
  (props.classDetail.enrollments || []).filter((enrollment) =>
    visibleStatuses.value.includes(enrollmentStatus(enrollment)),
  ),
);

function toggleStatus(status: string) {
  visibleStatuses.value = visibleStatuses.value.includes(status)
    ? visibleStatuses.value.filter((s) => s !== status)
    : [...visibleStatuses.value, status];
}

function confirmDrop(enrollmentDocumentId: string, studentName: string) {
  $q.dialog({
    title: t('SCHOOL_CLASS_DROP'),
    message: t('SCHOOL_CLASS_DROP_CONFIRM', { name: studentName }),
    cancel: t('CANCEL'),
    ok: { label: t('SCHOOL_CLASS_DROP'), color: 'negative' },
    persistent: true,
  }).onOk(() => emit('decide', enrollmentDocumentId, 'drop'));
}

function confirmReinstate(enrollmentDocumentId: string, studentName: string) {
  $q.dialog({
    title: t('SCHOOL_CLASS_REINSTATE'),
    message: t('SCHOOL_CLASS_REINSTATE_CONFIRM', { name: studentName }),
    cancel: t('CANCEL'),
    ok: { label: t('SCHOOL_CLASS_REINSTATE'), color: 'positive' },
    persistent: true,
  }).onOk(() => emit('decide', enrollmentDocumentId, 'reinstate'));
}
</script>

<template>
  <q-card
    bordered
    class="students-card"
  >
    <q-card-section class="q-pb-sm">
      <div class="text-h6">{{ $t('STUDENTS') }}</div>
      <div class="text-caption text-muted">
        {{
          `${visibleEnrollments.length} / ${(classDetail.enrollments || []).length}`
        }}
      </div>
      <div class="row q-gutter-xs q-mt-sm">
        <q-chip
          v-for="status in STATUS_ORDER"
          :key="status"
          clickable
          dense
          :outline="!visibleStatuses.includes(status)"
          color="secondary"
          :text-color="visibleStatuses.includes(status) ? 'dark' : undefined"
          :label="`${$t(`SCHOOL_STATUS_${status.toUpperCase()}`)} ${statusCounts[status] || 0}`"
          @click="toggleStatus(status)"
        />
      </div>
    </q-card-section>
    <q-separator />
    <q-list class="q-pa-sm">
      <q-item
        v-for="(enrollment, enrollmentIndex) in visibleEnrollments"
        :key="
          enrollment.documentId ||
          enrollment.student?.documentId ||
          enrollmentIndex
        "
        class="student-row q-mb-sm"
      >
        <q-item-section class="student-main-section">
          <q-item-label>{{
            schoolUserDisplayName(enrollment.student)
          }}</q-item-label>
          <q-item-label
            caption
            class="student-meta"
          >
            <span v-if="enrollment.student?.email">{{
              enrollment.student.email
            }}</span>
            <span class="student-last-active">{{
              lastActiveText(enrollment.student?.lastActiveAt)
            }}</span>
          </q-item-label>
        </q-item-section>
        <q-item-section
          side
          class="student-status-section"
        >
          <div class="student-actions">
            <template
              v-if="canDecide && enrollment.documentId && isPending(enrollment)"
            >
              <q-btn
                dense
                unelevated
                size="sm"
                color="positive"
                :label="$t('SCHOOL_CLASS_APPROVE')"
                @click="emit('decide', enrollment.documentId, 'approve')"
              />
              <q-btn
                dense
                outline
                size="sm"
                color="negative"
                :label="$t('SCHOOL_CLASS_DECLINE')"
                @click="emit('decide', enrollment.documentId, 'decline')"
              />
            </template>

            <SchoolEnrollmentStatusBadge
              align
              :status="enrollment.status || enrollment.enrollment_status"
            />

            <div class="student-action-icons">
              <q-btn
                round
                flat
                size="sm"
                class="action-icon"
                :icon="mdiChartBox"
                :disable="!enrollment.student?.documentId"
                @click="
                  enrollment.student?.documentId &&
                    emit(
                      'view-stats',
                      enrollment.student.documentId,
                      schoolUserDisplayName(enrollment.student),
                    )
                "
              >
                <q-tooltip>{{ $t('VIEW_STATISTICS') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-if="canDecide && enrollment.documentId && isActive(enrollment)"
                round
                flat
                size="sm"
                color="negative"
                class="action-icon"
                :icon="mdiAccountRemoveOutline"
                @click="
                  enrollment.documentId &&
                    confirmDrop(
                      enrollment.documentId,
                      schoolUserDisplayName(enrollment.student),
                    )
                "
              >
                <q-tooltip>{{ $t('SCHOOL_CLASS_DROP') }}</q-tooltip>
              </q-btn>
              <q-btn
                v-else-if="
                  canDecide && enrollment.documentId && isReinstatable(enrollment)
                "
                round
                flat
                size="sm"
                color="positive"
                class="action-icon"
                :icon="mdiAccountReactivateOutline"
                @click="
                  enrollment.documentId &&
                    confirmReinstate(
                      enrollment.documentId,
                      schoolUserDisplayName(enrollment.student),
                    )
                "
              >
                <q-tooltip>{{ $t('SCHOOL_CLASS_REINSTATE') }}</q-tooltip>
              </q-btn>
              <span
                v-else
                class="action-icon-spacer"
                aria-hidden="true"
              />
            </div>
          </div>
        </q-item-section>
      </q-item>
      <q-item v-if="!visibleEnrollments.length">
        <q-item-section>
          <q-item-label class="text-muted">{{
            (classDetail.enrollments || []).length
              ? $t('SCHOOL_NO_STUDENTS_MATCH_FILTER')
              : $t('NO_DATA_AVAILIABLE')
          }}</q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </q-card>
</template>

<style scoped>
.students-card {
  border-radius: 12px;
}

.student-row {
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface-card);
}

.student-row :deep(.q-item__label) {
  overflow-wrap: break-word;
}

.student-main-section {
  min-width: 0;
}

/* Email · last active on one line; the dot separator drops when it wraps. */
.student-meta {
  display: flex;
  flex-wrap: wrap;
  column-gap: 6px;
}

.student-meta > span + span::before {
  content: '·';
  margin-right: 6px;
}

.student-status-section {
  flex: 0 0 auto;
}

/* Right-hand action area: fixed columns so chips and icons line up across rows. */
.student-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* The status indicator itself lives in SchoolEnrollmentStatusBadge. */

.student-action-icons {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
}

.action-icon,
.action-icon-spacer {
  width: 34px;
  height: 34px;
}

/* Neutral grey for non-semantic icons; leave drop red and reinstate green. */
.action-icon:not(.text-negative):not(.text-positive) {
  color: var(--text-muted);
}

.action-icon:hover {
  background: rgba(0, 0, 0, 0.05);
}

:global(.body--dark .action-icon:hover) {
  background: rgba(255, 255, 255, 0.08);
}

.action-icon.text-negative:hover {
  background: rgba(214, 50, 48, 0.1);
}

.action-icon-spacer {
  display: inline-block;
}

@media (max-width: 599px) {
  .students-card :deep(.q-card__section),
  .students-card :deep(.q-list) {
    padding: 12px;
  }

  .students-card :deep(.q-item) {
    align-items: center;
  }

  .students-card :deep(.q-item__section--side) {
    width: auto;
    margin-top: 0;
    align-items: flex-end;
  }
}
</style>
